/*
Purpose: Paginated list view of analysis jobs without order grouping.
Audience: Results shell delegates to this component for job-only views.
Props:
- jobs: Array<Job>
- resultsByJobId: Record<jobId, {...}>
- onOpenJob(jobId): () => void // navigate to single-job view
- hasMore: boolean
- isLoading: boolean
- onLoadMore: () => void
*/
import React, { useMemo, useRef, useState, useEffect } from 'react';
import { FaCompressArrowsAlt, FaExpandArrowsAlt, FaList, FaBolt, FaRobot, FaSpinner } from 'react-icons/fa';
import { BiTargetLock } from 'react-icons/bi';
import '../styles/JobsListView.css';
import JobCard from '../../results/display/cards/JobCard';
import { JobCardSkeleton } from '../../common/SkeletonLoaders';
import { useJobCardCollapse } from '../../../hooks/useJobCardCollapse';
import CollapsibleVideoPlayer from '../../results/display/shared/CollapsibleVideoPlayer';
import AIAnalysisComponent from '../../analysis/AIAnalysisComponent';
import BountyComponent from '../../bounty/BountyComponent';

const JobsListView = ({
  jobs,
  resultsByJobId,
  onOpenJob,
  hasMore,
  isLoading,
  onLoadMore,
  sharedVideoUrl = null, // Optional: URL for the shared evidence video
  sharedVideoMetadata = null, // Optional: metadata for the video
  // Evidence and user info for Actions tab
  evidence = null,
  casefileId = null,
  casefileName = null,
  currentUser = null,
  onEvidenceUpdate = null
}) => {
  // Debug: Log jobs received
  useEffect(() => {
    console.log('📊 JobsListView received jobs:', {
      count: jobs.length,
      jobs: jobs.map(j => ({
        id: j.id,
        status: j.status,
        createdAt: j.createdAt?.toDate?.() || j.createdAt,
        evidenceId: j.evidenceId
      }))
    });
  }, [jobs]);

  // Main tab state: 'jobs' or 'actions'
  const [activeMainTab, setActiveMainTab] = useState('actions');
  
  // Actions sub-tab state: 'scan' or 'bounty'
  const [activeActionTab, setActiveActionTab] = useState('scan');
  
  // Filters and sorting (local to list view)
  const [providerFilter, setProviderFilter] = useState('all'); // 'all' | providerId substring match
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'processing' | 'completed' | 'failed'
  const [targetFilter, setTargetFilter] = useState('all'); // 'all' | target id
  const [sortBy, setSortBy] = useState('date_desc'); // 'date_desc' | 'date_asc' | 'cost_desc' | 'cost_asc'
  const [filtersExpanded, setFiltersExpanded] = useState(false); // Collapsible on mobile

  const providerOptions = useMemo(() => {
    const set = new Set();
    jobs.forEach(job => {
      if (job.provider) {
        set.add(job.provider.toLowerCase());
      }
    });
    return Array.from(set).filter(Boolean);
  }, [jobs]);

  const targetOptions = useMemo(() => {
    const set = new Set();
    jobs.forEach(job => {
      const targets = job.analysisRequest?.targets || job.selectedTargets || job.selectedServices || [];
      targets.forEach(t => set.add(t));
    });
    return Array.from(set);
  }, [jobs]);

  // Use collapse hook for managing job card states
  const {
    handleCollapseChange,
    areAllCollapsed,
    toggleAllCollapse,
    isJobCollapsed
  } = useJobCardCollapse(jobs);

  // Separate processing jobs to show them prominently at top
  const processingJobs = useMemo(() => {
    return jobs.filter(job => job.status === 'processing');
  }, [jobs]);

  const filteredJobs = useMemo(() => {
    let list = jobs.slice();
    
    if (providerFilter !== 'all') {
      list = list.filter(job => 
        job.provider && job.provider.toLowerCase().includes(providerFilter)
      );
    }
    
    if (targetFilter !== 'all') {
      list = list.filter(job => {
        const targets = job.analysisRequest?.targets || job.selectedTargets || job.selectedServices || [];
        return targets.includes(targetFilter);
      });
    }
    
    if (statusFilter !== 'all') {
      list = list.filter(job => job.status === statusFilter);
    }
    
    // Sorting - but always put processing jobs first unless filtering by status
    list.sort((a, b) => {
      // Processing jobs always come first (unless filtering by specific status)
      if (statusFilter === 'all') {
        if (a.status === 'processing' && b.status !== 'processing') return -1;
        if (b.status === 'processing' && a.status !== 'processing') return 1;
      }
      
      const dateA = a.createdAt?.toDate?.() || new Date(0);
      const dateB = b.createdAt?.toDate?.() || new Date(0);
      const costA = a.actualCost || 0;
      const costB = b.actualCost || 0;
      
      switch (sortBy) {
        case 'date_asc': return dateA - dateB;
        case 'cost_desc': return costB - costA;
        case 'cost_asc': return costA - costB;
        case 'date_desc':
        default: return dateB - dateA;
      }
    });
    
    return list;
  }, [jobs, providerFilter, statusFilter, targetFilter, sortBy]);

  const containerRef = useRef(null);
  
  // Video state management
  const [videoUrl, setVideoUrl] = useState(sharedVideoUrl);
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoError, setVideoError] = useState(null);
  const [videoCollapsed, setVideoCollapsed] = useState(false);
  
  // Update video URL when prop changes
  useEffect(() => {
    if (sharedVideoUrl !== videoUrl) {
      setVideoUrl(sharedVideoUrl);
      setVideoError(null);
    }
  }, [sharedVideoUrl]);
  
  // Extract video metadata from first job if not provided
  const videoMetadata = useMemo(() => {
    if (sharedVideoMetadata) return sharedVideoMetadata;
    
    // Try to get metadata from first job with evidence
    const firstJobWithEvidence = jobs.find(job => {
      const meta = resultsByJobId[job.id];
      return meta?.evidenceUrl || meta?.videoUrl;
    });
    
    if (firstJobWithEvidence) {
      const meta = resultsByJobId[firstJobWithEvidence.id];
      return {
        timestamp: firstJobWithEvidence.createdAt,
        duration: meta?.duration,
        size: meta?.fileSize
      };
    }
    
    return null;
  }, [jobs, resultsByJobId, sharedVideoMetadata]);

  return (
    <div
      ref={containerRef}
      className="jobs-list"
      role="region"
      aria-label={`Analysis jobs, ${filteredJobs.length} jobs`}
      aria-live="polite"
      aria-atomic="false"
    >
      {/* Collapsible Video Player - shown when video URL is available */}
      {(videoUrl || sharedVideoUrl) && (
        <CollapsibleVideoPlayer
          videoUrl={videoUrl || sharedVideoUrl}
          videoTitle={evidence?.fileName || 'Evidence Video'}
          videoMetadata={videoMetadata}
          isLoading={videoLoading}
          error={videoError}
          defaultCollapsed={videoCollapsed}
          onCollapseChange={setVideoCollapsed}
          onVideoError={(error) => {
            console.error('Video error:', error);
            setVideoError('Failed to load video');
          }}
          className="jobs-list-video"
          maxHeight="400px"
          fitMode="contain"
        />
      )}
      
      {/* Main Tabs: Actions vs Results */}
      <div className="jobs-main-tabs">
        <button
          className={`main-tab ${activeMainTab === 'actions' ? 'active' : ''}`}
          onClick={() => setActiveMainTab('actions')}
          disabled={!evidence}
        >
          <FaBolt />
          <span>Actions</span>
        </button>
        <button
          className={`main-tab ${activeMainTab === 'jobs' ? 'active' : ''}`}
          onClick={() => setActiveMainTab('jobs')}
        >
          <FaList />
          <span>Results</span>
          <span className="tab-count">{filteredJobs.length}</span>
        </button>
      </div>
      
      {/* Filter Bar - Only shown in Jobs tab */}
      {activeMainTab === 'jobs' && (
      <div className="jobs-filter-container">
        <button 
          className="filter-toggle-btn"
          onClick={() => setFiltersExpanded(!filtersExpanded)}
          aria-expanded={filtersExpanded}
          aria-controls="jobs-filter-controls"
        >
          <span className="filter-toggle-icon">{filtersExpanded ? '▼' : '▶'}</span>
          <span className="filter-toggle-text">Filters</span>
          {!filtersExpanded && (
            <span className="filter-summary">
              {providerFilter !== 'all' || targetFilter !== 'all' || statusFilter !== 'all' 
                ? '•' 
                : ''} {sortBy === 'date_desc' ? 'Newest' : sortBy === 'date_asc' ? 'Oldest' : 'Custom'}
            </span>
          )}
        </button>
        
        {filtersExpanded && (
          <div 
            id="jobs-filter-controls"
            className="jobs-filter-bar"
            role="toolbar"
            aria-label="Filter and sort analysis jobs"
          >
            <div className="filter-group">
              <label className="filter-label">
                Provider
                <select 
                  value={providerFilter} 
                  onChange={(e)=>setProviderFilter(e.target.value)} 
                  className="filter-select"
                >
                  <option value="all">All</option>
                  {providerOptions.map(opt => (
                    <option key={opt} value={opt}>{opt.replaceAll('-', ' ')}</option>
                  ))}
                </select>
              </label>
            </div>
            
            <div className="filter-group">
              <label className="filter-label">
                Targets
                <select 
                  value={targetFilter} 
                  onChange={(e)=>setTargetFilter(e.target.value)} 
                  className="filter-select"
                >
                  <option value="all">All</option>
                  {targetOptions.map(t => (
                    <option key={t} value={t}>{t.replaceAll('_', ' ')}</option>
                  ))}
                </select>
              </label>
            </div>
            
            <div className="filter-group">
              <label className="filter-label">
                Status
                <select 
                  value={statusFilter} 
                  onChange={(e)=>setStatusFilter(e.target.value)} 
                  className="filter-select"
                >
                  <option value="all">All</option>
                  <option value="processing">Processing</option>
                  <option value="completed">Completed</option>
                  <option value="failed">Failed</option>
                </select>
              </label>
            </div>
            
            <div className="filter-group">
              <label className="filter-label">
                Sort
                <select 
                  value={sortBy} 
                  onChange={(e)=>setSortBy(e.target.value)} 
                  className="filter-select"
                >
                  <option value="date_desc">Newest</option>
                  <option value="date_asc">Oldest</option>
                  <option value="cost_desc">Cost (high→low)</option>
                  <option value="cost_asc">Cost (low→high)</option>
                </select>
              </label>
            </div>
            
            {/* Collapse All Button */}
            <div className="filter-group">
              <button 
                className="collapse-all-btn"
                onClick={toggleAllCollapse}
                title={areAllCollapsed ? "Expand all job cards" : "Collapse all job cards"}
              >
                {areAllCollapsed ? (
                  <>
                    <FaExpandArrowsAlt />
                    <span>Expand All</span>
                  </>
                ) : (
                  <>
                    <FaCompressArrowsAlt />
                    <span>Collapse All</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Jobs Tab Content */}
      {activeMainTab === 'jobs' && (
        <>
          {/* Processing Jobs Banner - Always visible when there are processing jobs */}
          {processingJobs.length > 0 && statusFilter === 'all' && (
            <div className="processing-jobs-banner">
              <div className="processing-banner-header">
                <FaSpinner className="spinning" />
                <span>{processingJobs.length} Analysis{processingJobs.length > 1 ? 'es' : ''} in Progress</span>
              </div>
            </div>
          )}
          
          {/* Jobs List */}
          <div className="jobs-grid">
            {filteredJobs.map((job) => (
              <JobCard
                key={job.id}
                job={job}
                resultsMeta={resultsByJobId[job.id]}
                size="regular"
                onOpen={() => onOpenJob(job.id)}
                collapsible={true}
                isCollapsed={isJobCollapsed(job.id)}
                onCollapseChange={(isCollapsed) => handleCollapseChange(job.id, isCollapsed)}
              />
            ))}
          </div>
          
          {isLoading && (
            <div className="jobs-loading">
              <JobCardSkeleton />
              <JobCardSkeleton />
              <JobCardSkeleton />
            </div>
          )}
          
          <div className="load-more-section">
            {!isLoading && hasMore ? (
              <button 
                className="btn-secondary" 
                onMouseDown={(e)=>{ e.stopPropagation(); onLoadMore(); }}
              >
                Load more jobs
              </button>
            ) : !isLoading && !hasMore && filteredJobs.length > 0 ? (
              <div className="no-more-message">No more jobs</div>
            ) : !isLoading && filteredJobs.length === 0 ? (
              <div className="no-jobs-message">No jobs found</div>
            ) : null}
          </div>
        </>
      )}

      {/* Actions Tab Content */}
      {activeMainTab === 'actions' && evidence && (
        <div className="actions-tab-content">
          {/* Action Type Selector Buttons - Using evidence modal button style */}
          <div className="action-selector">
            <button
              className={`uew-action-btn scan ${activeActionTab === 'scan' ? 'active' : ''}`}
              onClick={() => setActiveActionTab('scan')}
            >
              <FaRobot />
              <span>Scan</span>
            </button>
            
            <button
              className={`uew-action-btn bounty ${activeActionTab === 'bounty' ? 'active' : ''}`}
              onClick={() => setActiveActionTab('bounty')}
            >
              <BiTargetLock />
              <span>Bounty</span>
            </button>
          </div>

          {/* Render Active Component */}
          <div className="action-component-container">
            {activeActionTab === 'scan' ? (
              <AIAnalysisComponent
                evidence={evidence}
                casefileId={casefileId}
                casefileName={casefileName}
                currentUser={currentUser}
                onUpdateSuccess={onEvidenceUpdate}
              />
            ) : (
              <BountyComponent
                evidence={evidence}
                currentUser={currentUser}
                casefileId={casefileId}
                casefileName={casefileName}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default JobsListView;
